import { create } from 'zustand';
import { CollabTask, TaskProgress } from '../services/teamCollabApi';

interface TeamCollabState {
  tasks: CollabTask[];
  loading: boolean;
  progressMap: Record<string, TaskProgress[]>;
  setTasks: (tasks: CollabTask[]) => void;
  mergeFromServer: (serverTasks: CollabTask[]) => void;
  addTask: (task: CollabTask) => void;
  updateTask: (id: string, updates: Partial<CollabTask>) => void;
  setLoading: (loading: boolean) => void;
  addProgress: (taskId: string, progress: TaskProgress) => void;
  clearProgress: (taskId: string) => void;
}

export const useTeamCollabStore = create<TeamCollabState>((set) => ({
  tasks: [], loading: false, progressMap: {},
  setTasks: (tasks) => set((state) => {
    const currentById = new Map(state.tasks.map((task) => [task.id, task]));
    return {
      tasks: tasks.map((task) => {
        const current = currentById.get(task.id);
        if (!current) return task;

        const currentThinking = current.thinkingContent || '';
        const nextThinking = task.thinkingContent || '';
        const shouldKeepLiveThinking =
          task.status === 'running' &&
          currentThinking.length > 0 &&
          currentThinking.length >= nextThinking.length;

        return {
          ...current,
          ...task,
          thinkingContent: shouldKeepLiveThinking ? currentThinking : task.thinkingContent,
        };
      }),
    };
  }),
  /**
   * 轮询专用：把服务端返回的任务合并到本地，不破坏 WebSocket 实时字段。
   * - 本地已有的运行中任务：只更新 status / result / summary / totalTokens 等后端确认字段，
   *   thinkingContent / progressMap 保持本地实时值不动
   * - 本地没有的新任务：直接追加
   * - 服务端没返回但在本地列表中的任务（刚创建等）：保留不丢
   */
  mergeFromServer: (serverTasks) => set((state) => {
    const currentById = new Map(state.tasks.map((t) => [t.id, t]));
    const serverIds = new Set(serverTasks.map((t) => t.id));
    const merged: CollabTask[] = [];

    // 服务端任务：合并进本地
    for (const st of serverTasks) {
      const cur = currentById.get(st.id);
      if (!cur) {
        merged.push(st);
        continue;
      }
      // 运行中的任务：只接后端的"确认性"字段，保留本地实时字段
      if (cur.status === 'running') {
        const curThink = cur.thinkingContent || '';
        const svThink = st.thinkingContent || '';
        merged.push({
          ...cur,
          status: st.status,
          result: st.result,
          summary: st.summary,
          resultType: st.resultType,
          resultFileId: st.resultFileId,
          resultFileName: st.resultFileName,
          totalTokens: st.totalTokens || cur.totalTokens,
          elapsedMs: st.elapsedMs || cur.elapsedMs,
          thinkingContent: curThink.length >= svThink.length ? curThink : svThink,
        } as CollabTask);
      } else {
        // 非运行中：直接拿服务端最新数据
        merged.push({ ...cur, ...st });
      }
    }

    // 服务端没返回的本地任务（刚创建还未入库等）：追加保留
    for (const t of state.tasks) {
      if (!serverIds.has(t.id)) {
        merged.push(t);
      }
    }

    return { tasks: merged };
  }),
  addTask: (task) => set((state) => ({ tasks: [task, ...state.tasks] })),
  updateTask: (id, updates) => set((state) => ({
    tasks: state.tasks.map((t) => (t.id === id ? { ...t, ...updates } : t)),
  })),
  setLoading: (loading) => set({ loading }),
  addProgress: (taskId, progress) => set((state) => ({
    progressMap: { ...state.progressMap, [taskId]: [...(state.progressMap[taskId] || []), progress] },
  })),
  clearProgress: (taskId) => set((state) => {
    const { [taskId]: _, ...rest } = state.progressMap;
    return { progressMap: rest };
  }),
}));
